import {
  createDefaultWorldConfig,
  getRoomMapPreset,
  SPIRIT_STONE_CLASS_CONFIGS,
  type SpiritStoneClassId,
} from "@xiuxian/game-core";
import {
  decodeServerMessage,
  encodeClientMessage,
  type JoinIntent,
  type ServerMessage,
  type SkinId,
  type TreasureKind,
  TreasureKindSchema,
} from "@xiuxian/game-protocol";
import {
  formatAscensionAnnouncement,
  formatFallbackCultivatorName,
  formatRoomEventLog,
  formatRuntimeError,
  formatRuntimeLog,
  formatRuntimeStatus,
  formatServerEventLog,
  formatWorldRebornAnnouncement,
} from "./i18n/hud-formatters.js";
import { DEFAULT_LOCALE, type Locale } from "./i18n/index.js";
import type { AudioCueKind, RuntimeStateStore } from "./runtime-state.js";

export interface RealtimeConnectionConfig {
  wsUrl: string;
  playerName: string;
  playerKey: string | null;
  anonymousPlayerId: string | null;
  locale: string;
  playerLevel: number;
  clientVersion: string;
  reconnectDelayMs: number;
  skinId?: SkinId | null;
  /** Absent => official quick play. Updated to private_join after a welcome. */
  joinIntent?: JoinIntent | null;
}

export class RealtimeClient {
  private socket: WebSocket | null = null;
  private readonly manuallyClosedSockets = new WeakSet<WebSocket>();
  private reconnectTimer: number | null = null;
  private closedManually = false;
  private connectionConfig: RealtimeConnectionConfig | null = null;
  private nextInputSeq = 1;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly store: RuntimeStateStore,
    private readonly getLocale: () => Locale = () => DEFAULT_LOCALE,
  ) {}

  connect(config: RealtimeConnectionConfig): void {
    const locale = this.getLocale();
    this.connectionConfig = config;
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) {
      return;
    }

    this.clearReconnectTimer();
    this.closedManually = false;
    this.store.patch({
      connectionStatus: "connecting",
      statusMessage: formatRuntimeStatus(locale, "connecting"),
      disconnectReason: null,
      lastError: null,
      lastSnapshot: null,
      lastLeaderboard: [],
      lastTreasureAnnouncement: null,
      lastTreasurePickup: null,
      lastTreasureGuard: null,
      welcome: null,
      chatMessages: [],
      lastServerError: null,
      reconnectAt: null,
    });
    this.store.recordLog(
      formatRuntimeLog(locale, "connect", { url: config.wsUrl }),
      locale,
    );

    const socket = new WebSocket(config.wsUrl);
    this.socket = socket;

    socket.addEventListener("open", () => {
      if (this.socket !== socket || !this.connectionConfig) {
        return;
      }
      this.store.patch({
        statusMessage: formatRuntimeStatus(this.getLocale(), "websocketOpen"),
      });
      this.store.recordLog(
        formatRuntimeLog(this.getLocale(), "helloSent"),
        this.getLocale(),
      );
      socket.send(
        encodeClientMessage({
          type: "client_hello",
          playerName: this.connectionConfig.playerName,
          ...(this.connectionConfig.playerKey
            ? { playerKey: this.connectionConfig.playerKey }
            : {}),
          ...(this.connectionConfig.anonymousPlayerId
            ? { anonymousPlayerId: this.connectionConfig.anonymousPlayerId }
            : {}),
          locale: this.connectionConfig.locale,
          requestedLevel: this.connectionConfig.playerLevel,
          ...(this.connectionConfig.skinId
            ? { skinId: this.connectionConfig.skinId }
            : {}),
          ...(this.connectionConfig.joinIntent
            ? { joinIntent: this.connectionConfig.joinIntent }
            : {}),
          clientVersion: this.connectionConfig.clientVersion,
        }),
      );
    });

    socket.addEventListener("message", (event) => {
      if (this.socket !== socket) {
        return;
      }
      this.handleMessage(event.data);
    });

    socket.addEventListener("error", () => {
      if (this.socket !== socket) {
        return;
      }
      this.store.recordAudioCue("ui_error");
      this.store.patch({
        connectionStatus: "error",
        lastError: formatRuntimeError(this.getLocale(), "transport"),
        statusMessage: formatRuntimeStatus(this.getLocale(), "transportError"),
      });
      this.store.recordLog(
        formatRuntimeLog(this.getLocale(), "transportError"),
        this.getLocale(),
      );
    });

    socket.addEventListener("close", (event) => {
      if (this.socket === socket) {
        this.socket = null;
      }
      this.clearHeartbeatTimer();
      if (this.closedManually || this.manuallyClosedSockets.has(socket)) {
        return;
      }
      const reason =
        event.reason.trim() || `close code ${event.code.toString()}`;
      this.store.patch({
        connectionStatus: "disconnected",
        disconnectReason: reason,
        statusMessage: formatRuntimeStatus(
          this.getLocale(),
          "disconnectedRetry",
        ),
      });
      this.store.recordLog(
        formatRuntimeLog(this.getLocale(), "close", { reason }),
        this.getLocale(),
      );
      this.scheduleReconnect();
    });
  }

  disconnect(): void {
    this.closedManually = true;
    this.clearReconnectTimer();
    this.clearHeartbeatTimer();
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      this.manuallyClosedSockets.add(socket);
    }
    socket?.close(1000, "runtime_shutdown");
    this.store.patch({
      connectionStatus: "disconnected",
      statusMessage: formatRuntimeStatus(this.getLocale(), "manualDisconnect"),
      reconnectAt: null,
    });
  }

  restart(config: RealtimeConnectionConfig): void {
    this.connectionConfig = config;
    this.clearReconnectTimer();
    const socket = this.socket;
    if (!socket || socket.readyState === WebSocket.CLOSED) {
      this.socket = null;
      this.closedManually = false;
      this.connect(config);
      return;
    }

    this.closedManually = true;
    this.socket = null;
    this.manuallyClosedSockets.add(socket);
    this.store.patch({
      connectionStatus: "connecting",
      statusMessage: formatRuntimeStatus(this.getLocale(), "reconnecting"),
      reconnectAt: null,
    });

    let didReconnect = false;
    let fallbackTimer: number | null = null;
    const reconnectAfterClose = () => {
      if (didReconnect) {
        return;
      }
      didReconnect = true;
      if (fallbackTimer !== null) {
        window.clearTimeout(fallbackTimer);
      }
      this.closedManually = false;
      this.connect(config);
    };

    socket.addEventListener("close", reconnectAfterClose, { once: true });
    fallbackTimer = window.setTimeout(reconnectAfterClose, 1200);
    socket.close(1000, "runtime_restart");
  }

  sendInput(angle: number, boost: boolean): void {
    if (!this.canSendGameplayMessage()) {
      return;
    }

    this.socket?.send(
      encodeClientMessage({
        type: "client_input",
        inputSeq: this.nextInputSeq,
        angle,
        boost,
        clientTime: Date.now(),
      }),
    );
    this.nextInputSeq += 1;
  }

  requestRespawn(): void {
    if (!this.canSendGameplayMessage()) {
      return;
    }

    this.socket?.send(
      encodeClientMessage({
        type: "client_respawn",
      }),
    );
    this.store.recordLog(
      formatRuntimeLog(this.getLocale(), "respawnRequested"),
      this.getLocale(),
    );
  }

  /** Private-room owner: start the waiting match. */
  startRoom(): void {
    if (
      !this.socket ||
      this.socket.readyState !== WebSocket.OPEN ||
      this.store.getState().welcome === null
    ) {
      return;
    }
    this.socket.send(encodeClientMessage({ type: "client_room_start" }));
  }

  /** Private-room owner: kick a player. */
  kickPlayer(targetId: string): void {
    if (!this.canSendRoomControl()) {
      return;
    }
    this.socket?.send(
      encodeClientMessage({ type: "client_room_kick", targetId }),
    );
  }

  /**
   * Private-room waiting lobby: set a member's team. The server authorizes —
   * a player may set their own team, the owner may set anyone's.
   */
  setTeam(targetPlayerId: string, teamId: "azure" | "crimson"): void {
    if (!this.canSendRoomControl()) {
      return;
    }
    this.socket?.send(
      encodeClientMessage({
        type: "client_room_set_team",
        targetPlayerId,
        teamId,
      }),
    );
  }

  /** Private-room owner: rotate the invite code. */
  resetInvite(): void {
    if (!this.canSendRoomControl()) {
      return;
    }
    this.socket?.send(
      encodeClientMessage({ type: "client_room_reset_invite" }),
    );
  }

  /** Private-room owner: restart an ended match. */
  rematch(): void {
    if (!this.canSendRoomControl()) {
      return;
    }
    this.socket?.send(encodeClientMessage({ type: "client_room_rematch" }));
  }

  private canSendRoomControl(): boolean {
    return (
      !!this.socket &&
      this.socket.readyState === WebSocket.OPEN &&
      this.store.getState().welcome !== null
    );
  }

  /** Private-room chat. The server validates, rate-limits and broadcasts. */
  sendChat(text: string): void {
    const trimmed = text.trim();
    if (trimmed.length === 0 || !this.canSendRoomControl()) {
      return;
    }
    this.socket?.send(
      encodeClientMessage({ type: "client_chat", text: trimmed }),
    );
  }

  /** Eliminated spectator: switch the watched target. */
  setSpectateTarget(targetId: string): void {
    if (
      !this.socket ||
      this.socket.readyState !== WebSocket.OPEN ||
      this.store.getState().welcome === null
    ) {
      return;
    }
    this.socket.send(
      encodeClientMessage({ type: "client_spectate_target", targetId }),
    );
    const state = this.store.getState();
    const selfId =
      state.lastSnapshot?.self.id ?? state.welcome?.playerId ?? null;
    if (selfId) {
      // Optimistically move the camera; the server confirms via AOI focus.
      this.store.patch({
        deathSpectate: { selfId, targetId, startedAt: Date.now() },
      });
    }
  }

  sendViewport(width: number, height: number, zoom = 1): void {
    if (!this.canSendGameplayMessage()) {
      return;
    }

    this.socket?.send(
      encodeClientMessage({
        type: "client_viewport",
        width,
        height,
        zoom,
        clientTime: Date.now(),
      }),
    );
  }

  private canSendGameplayMessage(): boolean {
    return (
      !!this.socket &&
      this.socket.readyState === WebSocket.OPEN &&
      this.store.getState().welcome !== null
    );
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== "string") {
      this.store.patch({
        connectionStatus: "error",
        lastError: formatRuntimeError(this.getLocale(), "nonTextMessage"),
      });
      this.store.recordLog(
        formatRuntimeLog(this.getLocale(), "nonTextIgnored"),
        this.getLocale(),
      );
      return;
    }

    let message: ServerMessage;
    try {
      message = decodeServerMessage(data);
    } catch (error) {
      const detail =
        error instanceof Error ? error.message : "Unknown protocol error";
      this.store.patch({
        connectionStatus: "error",
        lastError: detail,
        statusMessage: formatRuntimeStatus(
          this.getLocale(),
          "protocolParseFailed",
        ),
      });
      this.store.recordLog(
        formatRuntimeLog(this.getLocale(), "protocolError", { detail }),
        this.getLocale(),
      );
      return;
    }

    this.store.incrementMessageCount();
    this.applyMessage(message);
  }

  private applyMessage(message: ServerMessage): void {
    switch (message.type) {
      case "server_welcome": {
        this.nextInputSeq = 1;
        this.startHeartbeatTimer(message.heartbeatIntervalMs);
        const room = message.room ?? null;
        // Seed the waiting lobby for a freshly created private room so the
        // owner sees themselves before anyone else joins (the server only
        // broadcasts room state once a second player joins). Never clobber a
        // real room_state that already arrived for this room — for a joining
        // guest, the broadcast can land just before the welcome.
        const existingWaitingRoom = this.store.getState().waitingRoom;
        const hasFreshRoomState =
          existingWaitingRoom?.roomId === message.roomId;
        const seededWaitingRoom =
          !hasFreshRoomState &&
          room &&
          room.kind === "private" &&
          room.state === "waiting"
            ? {
                type: "server_room_state" as const,
                protocolVersion: message.protocolVersion,
                roomId: message.roomId,
                inviteCode: room.inviteCode ?? null,
                state: room.state,
                ownerId: room.ownerId,
                ...(room.config ? { config: room.config } : {}),
                players: [
                  {
                    id: message.playerId,
                    name: this.connectionConfig?.playerName ?? message.playerId,
                    joinedAt: Date.now(),
                    isOwner: room.ownerId === message.playerId,
                    isBot: false,
                  },
                ],
              }
            : null;
        // Pin reconnects to the room we actually landed in, so a dropped
        // connection (including the owner's) rejoins instead of recreating.
        if (
          this.connectionConfig &&
          room?.kind === "private" &&
          room.inviteCode
        ) {
          this.connectionConfig = {
            ...this.connectionConfig,
            joinIntent: {
              kind: "private_join",
              inviteCode: room.inviteCode,
            },
          };
        }
        // Match the rendered world size to the room's actual arena: private
        // rooms shrink the arena by map size, so derive the radius from the
        // shared map preset (official/plain paths keep the default).
        const arenaRadius = room?.config
          ? getRoomMapPreset(room.config.mapSize).arenaRadius
          : createDefaultWorldConfig().arenaRadius;
        this.store.patch({
          connectionStatus: "ready",
          welcome: message,
          roomInfo: room,
          coreBaseline: {
            ...createDefaultWorldConfig(),
            arenaRadius,
          },
          ...(seededWaitingRoom
            ? { waitingRoom: seededWaitingRoom }
            : room
              ? {}
              : { waitingRoom: null }),
          statusMessage: formatRuntimeStatus(this.getLocale(), "joinedRoom", {
            roomId: message.roomId,
          }),
          lastError: null,
          deathSpectate: null,
          matchSettlement: null,
        });
        this.store.recordLog(
          formatRuntimeLog(this.getLocale(), "handshake", {
            playerId: message.playerId,
            roomId: message.roomId,
          }),
          this.getLocale(),
        );
        this.sendViewport(window.innerWidth, window.innerHeight);
        return;
      }
      case "server_snapshot": {
        const spectate = this.store.getState().deathSpectate;
        const spectateTarget = spectate
          ? message.visiblePlayers.find(
              (player) => player.id === spectate.targetId,
            )
          : null;
        const spectatingKiller =
          !message.self.isAlive && spectate !== null && spectateTarget?.isAlive;
        this.store.patch({
          connectionStatus: "ready",
          lastSnapshot: message,
          ...(message.leaderboard
            ? { lastLeaderboard: message.leaderboard }
            : {}),
          ...(message.periodLeaderboards
            ? { lastPeriodLeaderboards: message.periodLeaderboards }
            : {}),
          statusMessage: message.self.isAlive
            ? formatRuntimeStatus(this.getLocale(), "syncedTick", {
                tick: message.serverTick,
              })
            : formatRuntimeStatus(
                this.getLocale(),
                spectatingKiller ? "spectatingKiller" : "deadRestart",
              ),
        });
        return;
      }
      case "server_room_state": {
        this.store.patch({
          connectionStatus: "ready",
          waitingRoom: message,
          // Returning to the lobby (e.g. rematch) clears the settlement screen.
          ...(message.state === "waiting" ? { matchSettlement: null } : {}),
          ...(this.store.getState().roomInfo
            ? {
                roomInfo: {
                  ...this.store.getState().roomInfo!,
                  state: message.state,
                  ownerId: message.ownerId,
                },
              }
            : {}),
        });
        return;
      }
      case "server_chat": {
        this.store.recordChatMessage({
          id: `${message.senderId}:${message.sentAt.toString()}:${Math.random().toString(36).slice(2)}`,
          senderId: message.senderId,
          senderName: message.senderName,
          text: message.text,
          sentAt: message.sentAt,
        });
        return;
      }
      case "server_room_event": {
        if (message.event === "room_started") {
          this.store.recordAudioCue("ui_start");
        }
        if (message.event === "owner_transferred") {
          this.store.recordAudioCue("ui_focus");
        }
        if (message.event === "match_ended") {
          recordMatchSettlement(this.store, message);
          this.store.recordAudioCue("level_up", { volumeScale: 0.8 });
        }
        if (
          message.event === "player_kicked" &&
          message.payload.playerId ===
            (this.store.getState().welcome?.playerId ?? null)
        ) {
          this.store.recordAudioCue("ui_error");
          this.store.recordLog(
            formatRoomEventLog(this.getLocale(), message),
            this.getLocale(),
          );
          // Banned from rejoining; stop here instead of reconnecting.
          this.disconnect();
          return;
        }
        this.store.recordLog(
          formatRoomEventLog(this.getLocale(), message),
          this.getLocale(),
        );
        return;
      }
      case "server_event": {
        if (message.event === "pickup") {
          recordPickupEffect(this.store, message);
        }
        if (
          message.event === "attack_started" ||
          message.event === "attack_hit" ||
          message.event === "attack_missed"
        ) {
          recordAttackEffect(this.store, message);
        }
        if (
          message.event === "death" ||
          message.event === "drop" ||
          message.event === "respawn"
        ) {
          recordWorldAudioCue(this.store, message);
        }
        if (message.event === "death") {
          recordDeathSpectate(this.store, message);
          recordDropBurstEffect(this.store, message);
        }
        if (message.event === "score_settled") {
          recordScoreSettlement(this.store, message);
        }
        if (message.event === "ascension_started") {
          recordAscensionAnnouncement(this.store, message, this.getLocale());
        }
        if (message.event === "treasure_spawned") {
          recordTreasureAnnouncement(this.store, message);
          this.store.recordAudioCue("treasure_spawned", { volumeScale: 0.9 });
        }
        if (message.event === "treasure_picked") {
          recordTreasurePickup(this.store, message);
        }
        if (message.event === "treasure_guard") {
          recordTreasureGuard(this.store, message);
        }
        if (message.event === "world_reborn") {
          recordWorldReborn(this.store, message, this.getLocale());
        }
        this.store.patch({
          connectionStatus: "ready",
          statusMessage: formatRuntimeStatus(
            this.getLocale(),
            "eventReceived",
            {
              event: message.event,
            },
          ),
        });
        this.store.recordLog(
          formatServerEventLog(this.getLocale(), message),
          this.getLocale(),
        );
        return;
      }
      case "server_error": {
        this.store.recordAudioCue("ui_error");
        // Room join failures are terminal: the server closes the socket and a
        // reconnect would just loop. Stop retrying so the UI can return home.
        if (isTerminalRoomError(message.code)) {
          this.closedManually = true;
          this.clearReconnectTimer();
          if (this.socket) {
            this.manuallyClosedSockets.add(this.socket);
          }
        }
        this.store.patch({
          connectionStatus: "error",
          lastServerError: message,
          lastError: `${message.code}: ${message.message}`,
          statusMessage: formatRuntimeStatus(
            this.getLocale(),
            "serverErrorReturned",
          ),
        });
        this.store.recordLog(
          formatRuntimeLog(this.getLocale(), "serverError", {
            code: message.code,
          }),
          this.getLocale(),
        );
        return;
      }
    }
  }

  private startHeartbeatTimer(intervalMs: number): void {
    this.clearHeartbeatTimer();
    this.heartbeatTimer = setInterval(
      () => {
        if (!this.canSendGameplayMessage()) {
          return;
        }
        this.socket?.send(
          encodeClientMessage({
            type: "client_heartbeat",
            clientTime: Date.now(),
          }),
        );
      },
      Math.max(250, intervalMs),
    );
  }

  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer === null) {
      this.heartbeatTimer = null;
      return;
    }
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private scheduleReconnect(): void {
    if (
      this.closedManually ||
      this.reconnectTimer !== null ||
      !this.connectionConfig
    ) {
      return;
    }

    const reconnectAt = Date.now() + this.connectionConfig.reconnectDelayMs;
    this.store.patch({ reconnectAt });
    this.store.recordLog(
      formatRuntimeLog(this.getLocale(), "reconnectIn", {
        seconds: Math.round(this.connectionConfig.reconnectDelayMs / 1000),
      }),
      this.getLocale(),
    );

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.closedManually && this.connectionConfig) {
        this.connect(this.connectionConfig);
      }
    }, this.connectionConfig.reconnectDelayMs);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer === null) {
      return;
    }
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }
}

/** Room join failures the client should not retry (server closed the socket). */
function isTerminalRoomError(code: string): boolean {
  return (
    code === "room_not_found" ||
    code === "room_closed" ||
    code === "room_full" ||
    code === "invalid_room_config"
  );
}

function recordMatchSettlement(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_room_event" }>,
): void {
  const payload = message.payload;
  const rawStandings = Array.isArray(payload.standings)
    ? payload.standings
    : [];
  const standings = rawStandings.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    if (typeof record.id !== "string" || typeof record.name !== "string") {
      return [];
    }
    return [
      {
        id: record.id,
        name: record.name,
        score: typeof record.score === "number" ? record.score : 0,
        isAlive: record.isAlive === true,
        ...(typeof record.teamId === "string" ? { teamId: record.teamId } : {}),
      },
    ];
  });
  const rawTeams = Array.isArray(payload.teams) ? payload.teams : [];
  const teams = rawTeams.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) {
      return [];
    }
    const record = entry as Record<string, unknown>;
    if (typeof record.teamId !== "string") {
      return [];
    }
    return [
      {
        teamId: record.teamId,
        score: typeof record.score === "number" ? record.score : 0,
      },
    ];
  });
  store.patch({
    matchSettlement: {
      winnerId: typeof payload.winnerId === "string" ? payload.winnerId : null,
      winnerName:
        typeof payload.winnerName === "string" ? payload.winnerName : null,
      winCondition:
        typeof payload.winCondition === "string" ? payload.winCondition : "",
      winningTeam:
        typeof payload.winningTeam === "string" ? payload.winningTeam : null,
      teams,
      standings,
    },
  });
}

function recordScoreSettlement(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
): void {
  const score =
    typeof message.payload.score === "number" ? message.payload.score : 0;
  const reason =
    typeof message.payload.reason === "string"
      ? message.payload.reason
      : "unknown";
  store.patch({
    lastSettledScore: {
      score: Math.max(0, Math.floor(score)),
      reason,
    },
  });
}

function recordDeathSpectate(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
): void {
  const state = store.getState();
  const selfId = state.lastSnapshot?.self.id ?? null;
  const playerId =
    typeof message.payload.playerId === "string"
      ? message.payload.playerId
      : null;
  const killerId =
    typeof message.payload.killerId === "string"
      ? message.payload.killerId
      : null;
  if (!selfId || !playerId) {
    return;
  }
  const spectate = state.deathSpectate;
  if (spectate?.selfId === selfId && playerId === spectate.targetId) {
    if (killerId && killerId !== selfId && killerId !== playerId) {
      store.patch({
        deathSpectate: {
          ...spectate,
          targetId: killerId,
        },
      });
    } else {
      store.patch({ deathSpectate: null });
    }
    return;
  }
  if (playerId !== selfId) {
    return;
  }
  if (!killerId || killerId === selfId) {
    store.patch({ deathSpectate: null });
    return;
  }
  store.patch({
    deathSpectate: {
      selfId,
      targetId: killerId,
      startedAt: Date.now(),
    },
  });
}

function recordAscensionAnnouncement(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
  locale: Locale,
): void {
  const name =
    typeof message.payload.name === "string"
      ? message.payload.name
      : formatFallbackCultivatorName(locale);
  const totalScore =
    typeof message.payload.totalScore === "number"
      ? message.payload.totalScore
      : null;
  const ascenderId =
    typeof message.payload.playerId === "string"
      ? message.payload.playerId
      : "";
  const scoreGain =
    typeof message.payload.scoreGain === "number"
      ? message.payload.scoreGain
      : 0;
  const ritualMs =
    typeof message.payload.ritualMs === "number"
      ? message.payload.ritualMs
      : 7000;
  const startedAt = Date.now();
  const selfId = store.getState().welcome?.playerId ?? null;
  store.patch({
    lastAscensionAnnouncement: formatAscensionAnnouncement(locale, {
      name,
      totalScore,
    }),
    ascensionRitual: {
      ascenderId,
      ascenderName: name,
      scoreGain: Math.max(0, Math.floor(scoreGain)),
      totalScore: Math.max(0, Math.floor(totalScore ?? 0)),
      startedAt,
      endsAt: startedAt + Math.max(1000, Math.floor(ritualMs)),
      isSelf: ascenderId.length > 0 && ascenderId === selfId,
      phase: "ascending",
      rebornAt: null,
    },
  });
  store.recordAudioCue("level_up", {
    volumeScale: ascenderId === selfId ? 1 : 0.7,
  });
  store.recordAudioCue("reincarnation_fade", { volumeScale: 0.5 });
}

function recordWorldReborn(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
  locale: Locale,
): void {
  const ascenderName =
    typeof message.payload.ascenderName === "string"
      ? message.payload.ascenderName
      : (store.getState().ascensionRitual?.ascenderName ??
        formatFallbackCultivatorName(locale));
  const existing = store.getState().ascensionRitual;
  const now = Date.now();
  store.patch({
    lastAscensionAnnouncement: formatWorldRebornAnnouncement(
      locale,
      ascenderName,
    ),
    ascensionRitual: existing
      ? {
          ...existing,
          ascenderName,
          phase: "reborn",
          rebornAt: now,
        }
      : {
          ascenderId:
            typeof message.payload.ascenderId === "string"
              ? message.payload.ascenderId
              : "",
          ascenderName,
          scoreGain: 0,
          totalScore: 0,
          startedAt: now,
          endsAt: now,
          isSelf: false,
          phase: "reborn",
          rebornAt: now,
        },
  });
  store.recordAudioCue("ui_reincarnate", { volumeScale: 0.8 });
}

function recordTreasureAnnouncement(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
): void {
  const payload = message.payload;
  if (
    typeof payload.id !== "string" ||
    !isTreasureKind(payload.kind) ||
    typeof payload.x !== "number" ||
    typeof payload.y !== "number"
  ) {
    return;
  }
  store.recordTreasureAnnouncement({
    id: payload.id,
    kind: payload.kind,
    x: payload.x,
    y: payload.y,
  });
}

function recordTreasurePickup(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
): void {
  const payload = message.payload;
  if (
    typeof payload.id !== "string" ||
    !isTreasureKind(payload.kind) ||
    typeof payload.x !== "number" ||
    typeof payload.y !== "number"
  ) {
    return;
  }
  store.recordTreasurePickup({
    id: payload.id,
    kind: payload.kind,
    x: payload.x,
    y: payload.y,
  });
}

function recordTreasureGuard(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
): void {
  const payload = message.payload;
  if (
    typeof payload.playerId !== "string" ||
    typeof payload.attackerId !== "string" ||
    typeof payload.remainingCharges !== "number" ||
    typeof payload.x !== "number" ||
    typeof payload.y !== "number"
  ) {
    return;
  }
  store.recordTreasureGuard({
    playerId: payload.playerId,
    attackerId: payload.attackerId,
    remainingCharges: payload.remainingCharges,
    x: payload.x,
    y: payload.y,
  });
}

function isTreasureKind(value: unknown): value is TreasureKind {
  return TreasureKindSchema.safeParse(value).success;
}

function recordDropBurstEffect(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
): void {
  const payload = message.payload;
  if (
    typeof payload.playerId !== "string" ||
    typeof payload.x !== "number" ||
    typeof payload.y !== "number" ||
    typeof payload.droppedSpiritStoneCount !== "number" ||
    payload.droppedSpiritStoneCount <= 0
  ) {
    return;
  }
  store.recordDropBurstEffect({
    playerId: payload.playerId,
    x: payload.x,
    y: payload.y,
    value: payload.droppedSpiritStoneCount,
  });
}

function recordPickupEffect(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
): void {
  const payload = message.payload;
  if (
    typeof payload.playerId !== "string" ||
    typeof payload.stoneId !== "string" ||
    typeof payload.x !== "number" ||
    typeof payload.y !== "number" ||
    typeof payload.value !== "number"
  ) {
    return;
  }
  store.recordPickupEffect({
    playerId: payload.playerId,
    stoneId: payload.stoneId,
    x: payload.x,
    y: payload.y,
    value: payload.value,
  });
  const selfId = store.getState().lastSnapshot?.self.id ?? null;
  store.recordAudioCue(getPickupAudioCueKind(payload.value), {
    volumeScale: payload.playerId === selfId ? 1 : 0.45,
    detune: getPickupDetune(payload.stoneId),
  });
}

function recordAttackEffect(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
): void {
  const payload = message.payload;
  if (typeof payload.attackerId !== "string") {
    return;
  }
  const durationMs =
    typeof payload.windupMs === "number"
      ? payload.windupMs
      : typeof payload.recoveryMs === "number"
        ? Math.min(240, payload.recoveryMs)
        : 180;
  const result =
    message.event === "attack_started"
      ? "started"
      : message.event === "attack_hit"
        ? "hit"
        : "missed";
  store.recordAttackEffect({
    attackerId: payload.attackerId,
    targetId: typeof payload.targetId === "string" ? payload.targetId : null,
    attackerX: typeof payload.attackerX === "number" ? payload.attackerX : null,
    attackerY: typeof payload.attackerY === "number" ? payload.attackerY : null,
    targetX: typeof payload.targetX === "number" ? payload.targetX : null,
    targetY: typeof payload.targetY === "number" ? payload.targetY : null,
    facing: typeof payload.facing === "number" ? payload.facing : null,
    result,
    durationMs: result === "started" ? durationMs : Math.max(120, durationMs),
  });
  const selfId = store.getState().lastSnapshot?.self.id ?? null;
  const isSelfRelated =
    payload.attackerId === selfId ||
    (typeof payload.targetId === "string" && payload.targetId === selfId);
  const volumeScale = isSelfRelated ? 1 : 0.42;
  if (message.event === "attack_started") {
    store.recordAudioCue("sword_windup", { volumeScale });
    return;
  }
  store.recordAudioCue("sword_release", { volumeScale });
  store.recordAudioCue(
    message.event === "attack_hit" ? "sword_hit" : "sword_miss",
    { volumeScale },
  );
}

function recordWorldAudioCue(
  store: RuntimeStateStore,
  message: Extract<ServerMessage, { type: "server_event" }>,
): void {
  const payload = message.payload;
  const selfId = store.getState().lastSnapshot?.self.id ?? null;
  switch (message.event) {
    case "death": {
      const isSelfDeath = payload.playerId === selfId;
      store.recordAudioCue("death_scatter", {
        volumeScale: isSelfDeath ? 1 : 0.5,
      });
      if (isSelfDeath) {
        store.recordAudioCue("reincarnation_fade", { volumeScale: 0.72 });
      }
      return;
    }
    case "drop":
      store.recordAudioCue("stone_scatter", {
        volumeScale: payload.playerId === selfId ? 1 : 0.5,
      });
      return;
    case "respawn":
      if (payload.playerId === selfId) {
        store.recordAudioCue("ui_reincarnate", { volumeScale: 0.75 });
      }
      return;
  }
}

function getPickupAudioCueKind(value: number): AudioCueKind {
  const stoneClass = getSpiritStoneClassId(value);
  if (stoneClass === "condensed") {
    return "pickup_high";
  }
  return "pickup_low";
}

function getSpiritStoneClassId(value: number): SpiritStoneClassId {
  if (value >= SPIRIT_STONE_CLASS_CONFIGS.condensed.value) {
    return "condensed";
  }
  return "fragment";
}

function getPickupDetune(stoneId: string): number {
  let hash = 0;
  for (let index = 0; index < stoneId.length; index += 1) {
    hash = (hash * 31 + stoneId.charCodeAt(index)) >>> 0;
  }
  return (hash % 5) * 100 - 200;
}
